/**
 * Branches List Page
 * Display and manage all company branches
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import branchService from '../../services/branches';
import './Branches.css';

const Branches = () => {
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtering, setFiltering] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterActive, setFilterActive] = useState('true');
  const searchInputRef = useRef(null);
  const isInitialLoad = useRef(true);

  // Fetch branches when filters change
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        // Use filtering state for subsequent fetches to avoid re-rendering entire page
        if (isInitialLoad.current) {
          setLoading(true);
        } else {
          setFiltering(true);
        }
        setError(null);

        const filters = {};
        if (filterActive !== '') {
          filters.isActive = filterActive === 'true';
        }
        if (filterType) {
          filters.type = filterType;
        }
        if (appliedSearch) {
          filters.search = appliedSearch;
        }

        const data = await branchService.getBranches(filters);
        setBranches(data);
        isInitialLoad.current = false;
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load branches');
        console.error('Error fetching branches:', err);
      } finally {
        setLoading(false);
        setFiltering(false);
      }
    };

    fetchBranches();
  }, [filterType, filterActive, appliedSearch]);

  const handleSearch = (e) => {
    e.preventDefault();
    setAppliedSearch(searchTerm);
  };

  const handleSearchInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setAppliedSearch('');
    searchInputRef.current?.focus();
  };

  const handleDeleteBranch = async (branchId, branchName) => {
    if (window.confirm(`Are you sure you want to delete "${branchName}"? This will deactivate the branch.`)) {
      try {
        await branchService.deleteBranch(branchId);
        // Update local state to reflect the deletion (mark as inactive)
        setBranches(prev => prev.map(b =>
          b._id === branchId ? { ...b, isActive: false } : b
        ));
      } catch (err) {
        alert(err.response?.data?.message || 'Failed to delete branch');
      }
    }
  };

  const getStatusBadge = (isActive) => {
    return (
      <span className={`status-badge ${isActive ? 'status-active' : 'status-inactive'}`}>
        {isActive ? 'Active' : 'Inactive'}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const typeColors = {
      Main: 'type-main',
      Branch: 'type-branch',
      Hub: 'type-hub',
      Warehouse: 'type-warehouse',
    };

    return (
      <span className={`type-badge ${typeColors[type] || ''}`}>
        {type}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="branches-page">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading branches...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="branches-page">
      <div className="page-header">
        <div>
          <h1>Branch Management</h1>
          <p className="page-subtitle">Manage your company branches and locations</p>
        </div>
        <button className="btn-primary" onClick={() => navigate('/add-branch')}>
          <span>+</span> Add Branch
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="branches-controls">
        <form className="search-form" onSubmit={handleSearch}>
          <div className="search-input-wrapper">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search branches by name, code, or city..."
              value={searchTerm}
              onChange={handleSearchInputChange}
              className="search-input"
            />
            {searchTerm && (
              <button
                type="button"
                className="btn-clear-search"
                onClick={handleClearSearch}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
          <button type="submit" className="btn-search" disabled={filtering}>
            {filtering ? 'Searching...' : 'Search'}
          </button>
        </form>

        <div className="filters">
          <select
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value)}
            className="filter-select"
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="filter-select"
          >
            <option value="">All Types</option>
            <option value="Main">Main</option>
            <option value="Branch">Branch</option>
            <option value="Hub">Hub</option>
            <option value="Warehouse">Warehouse</option>
          </select>
        </div>
      </div>

      <div className="branches-stats">
        <div className="stat-card">
          <h3>{branches.length}</h3>
          <p>Total Branches</p>
        </div>
        <div className="stat-card">
          <h3>{branches.filter(b => b.isActive).length}</h3>
          <p>Active</p>
        </div>
        <div className="stat-card">
          <h3>{branches.filter(b => !b.isActive).length}</h3>
          <p>Inactive</p>
        </div>
      </div>

      {branches.length === 0 && !filtering ? (
        <div className="empty-state">
          <div className="empty-icon">📍</div>
          <h3>No branches found</h3>
          <p>{appliedSearch ? 'Try a different search term' : 'Get started by adding your first branch location'}</p>
          {!appliedSearch && (
            <button className="btn-primary" onClick={() => navigate('/add-branch')}>
              Add Branch
            </button>
          )}
          {appliedSearch && (
            <button className="btn-secondary" onClick={handleClearSearch}>
              Clear Search
            </button>
          )}
        </div>
      ) : (
        <div className={`branches-grid ${filtering ? 'filtering' : ''}`}>
          {branches.map((branch) => (
            <div key={branch._id} className="branch-card">
              <div className="branch-card-header">
                <div>
                  <h3>{branch.name}</h3>
                  <p className="branch-code">{branch.code}</p>
                </div>
                <div className="branch-badges">
                  {getTypeBadge(branch.type)}
                  {getStatusBadge(branch.isActive)}
                </div>
              </div>

              <div className="branch-card-body">
                <div className="branch-info-row">
                  <span className="info-icon">📍</span>
                  <div>
                    <p className="info-label">Address</p>
                    <p className="info-value">{branch.fullAddress || `${branch.address.street}, ${branch.address.city}, ${branch.address.state}`}</p>
                  </div>
                </div>

                <div className="branch-info-row">
                  <span className="info-icon">📞</span>
                  <div>
                    <p className="info-label">Contact</p>
                    <p className="info-value">{branch.contact.phone}</p>
                    {branch.contact.email && (
                      <p className="info-value-sub">{branch.contact.email}</p>
                    )}
                  </div>
                </div>

                {branch.contact.manager && (
                  <div className="branch-info-row">
                    <span className="info-icon">👤</span>
                    <div>
                      <p className="info-label">Manager</p>
                      <p className="info-value">{branch.contact.manager}</p>
                    </div>
                  </div>
                )}

                <div className="branch-info-row">
                  <span className="info-icon">🌐</span>
                  <div>
                    <p className="info-label">Coordinates</p>
                    <p className="info-value">{branch.latitude?.toFixed(4)}, {branch.longitude?.toFixed(4)}</p>
                  </div>
                </div>
              </div>

              <div className="branch-card-footer">
                <button
                  className="btn-secondary"
                  onClick={() => navigate(`/edit-branch/${branch._id}`)}
                >
                  Edit
                </button>
                <button
                  className="btn-danger"
                  onClick={() => handleDeleteBranch(branch._id, branch.name)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Branches;
